import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useDesk, workOrderHref } from '../lib/desk';
import { providerPath } from '../lib/integrations';
import { indentedName, useDepartments } from '../lib/automation';
import { formatDateTime } from '../lib/format';
import type {
  EmailIntegration,
  EmailService,
  GoogleIntegration,
  InboundEmail,
  InboundEmailOutcome,
  IntegrationProvider,
  OutboundEmail,
  OutboundEmailKind,
  OutboundEmailStatus,
  Page,
} from '../lib/types';
import { CopyField, DataSourcePicker, useIntegrationMutations } from './integration-shell';
import { Instructions, RemoveCredentials, Step, useCredentials } from './mdm-integrations';
import { useToast } from './toast';
import { Alert, Badge, Button, cn, EmptyState, Field, Input, Pagination, Select, Spinner, Switch, Table, Td, Textarea, Th } from './ui';

const SERVICES: { value: EmailService; title: string; text: string }[] = [
  { value: 'GOOGLE', title: 'Google Workspace', text: 'Gmail, through a service account with domain-wide delegation.' },
  { value: 'MICROSOFT', title: 'Microsoft 365', text: 'Outlook, through a Microsoft Entra app registration.' },
];

// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------

function GoogleMailAccess({ integration, google }: { integration: EmailIntegration; google?: GoogleIntegration }) {
  const { can } = useDesk();
  const isAdmin = can('ADMIN');
  const toast = useToast();
  const { refresh } = useIntegrationMutations(integration.provider);
  const saved = integration.email;
  const [source, setSource] = useState<'platform' | 'school'>(saved.schoolServiceAccount || !saved.platformServiceAccount ? 'school' : 'platform');
  const [keyJson, setKeyJson] = useState('');

  const saveKey = useMutation({
    mutationFn: (serviceAccountJson: string | null) => api.put(`/integrations/${providerPath(integration.provider)}/credentials`, { service: 'GOOGLE', serviceAccountJson }),
    onSuccess: () => {
      refresh();
      setKeyJson('');
      toast.success('Service account saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const shareGoogleKey = useMutation({
    mutationFn: () => api.post(`/integrations/${providerPath(integration.provider)}/use-google-key`),
    onSuccess: () => {
      refresh();
      toast.success('Using the Google integration’s service account');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const account = source === 'school' ? saved.schoolServiceAccount : saved.platformServiceAccount;
  const googleKey = google?.google.schoolServiceAccount ?? null;
  const chromebookAccount = google?.mode === 'LIVE' ? (google.google.schoolServiceAccount ?? google.google.platformServiceAccount) : null;
  // Google keeps one list of scopes per client ID, so an account that also syncs Chromebooks needs both sets.
  const sharesClientId = !!account?.clientId && account.clientId === chromebookAccount?.clientId;
  const scopes = sharesClientId && google ? [...google.google.scopes, ...saved.googleScopes] : saved.googleScopes;

  return (
    <>
      <Step number={2} title="Service account">
        <p className="-mt-2 text-sm text-slate-500">
          Edvora signs in as the mailbox with a Google Cloud service account and domain-wide delegation. It has to be a user’s mailbox, not a Google Group,
          though a group can deliver to it.
        </p>
        {saved.secretsUnreadable && <Alert tone="red">The saved key can’t be read. Upload it again.</Alert>}
        <div className="max-w-2xl space-y-2">
          <label className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', !saved.platformServiceAccount && 'opacity-60')}>
            <input type="radio" className="mt-1" checked={source === 'platform'} disabled={!isAdmin || !saved.platformServiceAccount} onChange={() => setSource('platform')} />
            <span>
              <span className="block text-sm font-medium text-slate-900">Edvora’s service account</span>
              <span className="block text-xs text-slate-500">
                {saved.platformServiceAccount ? 'Nothing to upload. Authorize its client ID below.' : 'Not configured on this Edvora environment.'}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 rounded-lg border px-4 py-3">
            <input type="radio" className="mt-1" checked={source === 'school'} disabled={!isAdmin} onChange={() => setSource('school')} />
            <span className="flex-1">
              <span className="block text-sm font-medium text-slate-900">Our own service account</span>
              <span className="block text-xs text-slate-500">
                {saved.schoolServiceAccount ? `Using ${saved.schoolServiceAccount.clientEmail}` : 'Upload a JSON key from your Google Cloud project.'}
              </span>
            </span>
          </label>
        </div>
        {source === 'school' && isAdmin && (
          <div className="max-w-2xl space-y-3 rounded-lg border border-slate-200 p-4">
            {googleKey && googleKey.clientEmail !== saved.schoolServiceAccount?.clientEmail && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                <span>The Google integration already has a key for {googleKey.clientEmail}.</span>
                <Button size="sm" loading={shareGoogleKey.isPending} onClick={() => shareGoogleKey.mutate()}>
                  Use that key
                </Button>
              </div>
            )}
            <input
              type="file"
              accept=".json,application/json"
              className="text-sm"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) setKeyJson(await file.text());
              }}
            />
            <Textarea
              rows={4}
              value={keyJson}
              onChange={(e) => setKeyJson(e.target.value)}
              placeholder={saved.schoolServiceAccount ? 'A key is saved. Paste or upload a new one to replace it.' : '{ "type": "service_account", … }'}
              className="font-mono text-xs"
            />
          </div>
        )}
        {isAdmin && (
          <Button
            loading={saveKey.isPending}
            disabled={source === 'school' ? !keyJson : !saved.schoolServiceAccount}
            onClick={() => saveKey.mutate(source === 'school' ? keyJson : null)}
          >
            {source === 'school' ? 'Save service account key' : 'Use Edvora’s service account'}
          </Button>
        )}
      </Step>

      <Step number={3} title="Authorize in Google Admin">
        <Instructions
          steps={[
            <>
              In the Google Admin console, go to <strong>Security › Access and data control › API controls › Manage domain-wide delegation</strong>.
            </>,
            <>
              Choose <strong>Add new</strong>, or edit the entry that already has this client ID, and enter the scopes below. Edvora reads the inbox and
              sends email from the mailbox: confirmations, technicians’ replies, and notices.
            </>,
          ]}
        />
        <div className="max-w-2xl space-y-4">
          {account?.clientId ? (
            <CopyField label="Client ID" value={account.clientId} />
          ) : (
            <Alert tone="amber">Save a service account first to see its client ID.</Alert>
          )}
          <CopyField
            label="OAuth scopes"
            value={scopes.join(',')}
            hint={sharesClientId ? 'This service account also syncs Chromebooks, so the list includes those scopes too. Saving only the Gmail scopes would stop that sync.' : undefined}
          />
        </div>
      </Step>
    </>
  );
}

function MicrosoftMailAccess({ integration }: { integration: EmailIntegration }) {
  const { can } = useDesk();
  const isAdmin = can('ADMIN');
  const { save, remove } = useCredentials(integration.provider);
  const saved = integration.email;
  const [clientId, setClientId] = useState(saved.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');

  return (
    <Step number={2} title="App registration">
      <Instructions
        steps={[
          <>
            In the Microsoft Entra admin center, go to <strong>App registrations › New registration</strong>. The app registration used for Intune works too.
          </>,
          <>
            Under <strong>API permissions</strong>, add the Microsoft Graph <strong>application</strong> permissions{' '}
            {saved.microsoftPermissions.map((permission, index) => (
              <span key={permission}>
                {index > 0 && ' and '}
                <strong>{permission}</strong>
              </span>
            ))}
            , then <strong>Grant admin consent</strong>.
          </>,
          <>
            Under <strong>Certificates &amp; secrets</strong>, create a client secret and copy its <strong>Value</strong>.
          </>,
          <>
            Recommended: in Exchange Online, limit the app to the help desk mailbox with an application access policy. Without one, the app can open every
            mailbox.
          </>,
        ]}
      />
      {saved.secretsUnreadable && <Alert tone="red">The saved credentials can’t be read. Enter them again.</Alert>}
      <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
        <Field label="Application (client) ID">
          <Input value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
        </Field>
        <Field label="Client secret">
          <Input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            disabled={!isAdmin}
            placeholder={saved.hasClientSecret ? 'Saved - leave blank to keep' : ''}
          />
        </Field>
      </div>
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => save.mutate({ service: 'MICROSOFT', clientId, ...(clientSecret ? { clientSecret } : {}) }, { onSuccess: () => setClientSecret('') })}
          >
            Save app registration
          </Button>
          {saved.clientId && (
            <RemoveCredentials
              provider={integration.provider}
              remove={remove}
              description="Email stops turning into tickets, and replies stop going out, until new credentials are saved. Tickets already created stay in Edvora."
            />
          )}
        </div>
      )}
    </Step>
  );
}

export function EmailConnection({ integration, google }: { integration: EmailIntegration; google?: GoogleIntegration }) {
  const { can } = useDesk();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations(integration.provider);
  const s = integration.settings;
  const facilities = integration.provider === 'FACILITIES_EMAIL';
  const [service, setService] = useState<EmailService>(s.service);
  const [mailbox, setMailbox] = useState(s.mailbox ?? '');
  const [directoryId, setDirectoryId] = useState(s.directoryId ?? '');
  useEffect(() => {
    setService(s.service);
    setMailbox(s.mailbox ?? '');
    setDirectoryId(s.directoryId ?? '');
  }, [s.service, s.mailbox, s.directoryId]);

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
        {integration.mode === 'DEMO' && (
          <p className="mt-3 max-w-2xl text-sm text-slate-500">
            Five sample emails arrive on the first check: two become tickets, one is a reply to one of them, and an automatic reply and a sales email are
            skipped. Each is imported once, and nothing is really emailed.
          </p>
        )}
      </section>

      {integration.mode === 'LIVE' && (
        <>
          <Step number={1} title={facilities ? 'Facilities mailbox' : 'Help desk mailbox'}>
            <p className="-mt-2 text-sm text-slate-500">
              {facilities ? 'The address people email for maintenance, repairs, and cleaning.' : 'The address people email for help.'} Edvora checks its inbox and sends
              email from it.
            </p>
            <div className="grid max-w-2xl gap-3 sm:grid-cols-2">
              {SERVICES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={!isAdmin}
                  onClick={() => setService(option.value)}
                  className={cn(
                    'rounded-lg border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed',
                    service === option.value ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500' : 'border-slate-200 hover:border-slate-300',
                  )}
                >
                  <p className="text-sm font-medium text-slate-900">{option.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{option.text}</p>
                </button>
              ))}
            </div>
            <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
              <Field label="Mailbox address">
                <Input type="email" value={mailbox} onChange={(e) => setMailbox(e.target.value)} disabled={!isAdmin} placeholder={facilities ? 'facilities@yourschool.org' : 'helpdesk@yourschool.org'} />
              </Field>
              {service === 'MICROSOFT' && (
                <Field label="Directory (tenant) ID" hint="On the Microsoft Entra admin center overview. Your onmicrosoft.com domain also works.">
                  <Input value={directoryId} onChange={(e) => setDirectoryId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
                </Field>
              )}
            </div>
            {service !== s.service && <Alert tone="amber">Save the mailbox to switch to {SERVICES.find((o) => o.value === service)?.title}.</Alert>}
            {isAdmin && (
              <Button
                loading={update.isPending}
                onClick={() => update.mutate({ settings: { service, mailbox, ...(service === 'MICROSOFT' ? { directoryId } : {}) } })}
              >
                Save mailbox
              </Button>
            )}
          </Step>

          {service === 'GOOGLE' ? <GoogleMailAccess integration={integration} google={google} /> : <MicrosoftMailAccess integration={integration} />}

          <Step number={service === 'GOOGLE' ? 4 : 3} title="Test and turn on">
            <Instructions
              steps={[
                <>
                  Choose <strong>Test connection</strong>. Edvora reads the inbox to make sure it can.
                </>,
                <>
                  Switch on <strong>Enabled</strong>. The first check brings in email from the last hour; older mail is left alone.
                </>,
                <>
                  Send an email to the mailbox and choose <strong>Check now</strong>. It shows up in the email log.
                </>,
              ]}
            />
          </Step>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Senders, tickets, and replies
// ---------------------------------------------------------------------------

export function EmailOptions({ integration }: { integration: EmailIntegration }) {
  const { can } = useDesk();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations(integration.provider);
  const departments = useDepartments();
  const facilities = integration.provider === 'FACILITIES_EMAIL';
  const s = integration.settings;
  const [allowedSenders, setAllowedSenders] = useState(s.allowedSenders);
  const [allowedDomains, setAllowedDomains] = useState(s.allowedDomains.join(', '));
  const [departmentId, setDepartmentId] = useState(s.departmentId ?? '');
  const [sendConfirmation, setSendConfirmation] = useState(s.sendConfirmation);
  const [replyByEmail, setReplyByEmail] = useState(s.replyByEmail);
  const [notifyTechnicians, setNotifyTechnicians] = useState(s.notifyTechnicians);
  const [emailOnResolve, setEmailOnResolve] = useState(s.emailOnResolve);
  const [reopenOnReply, setReopenOnReply] = useState(s.reopenOnReply);
  const [emailStatusUpdates, setEmailStatusUpdates] = useState(s.emailStatusUpdates ?? true);

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,36rem)_minmax(0,1fr)]">
      <div className="space-y-6">
        <Field label={facilities ? 'Who can email facilities' : 'Who can email the help desk'}>
          <Select value={allowedSenders} onChange={(e) => setAllowedSenders(e.target.value as typeof allowedSenders)} disabled={!isAdmin}>
            <option value="SCHOOL">People at the school</option>
            <option value="ANYONE">Anyone</option>
          </Select>
        </Field>
        {allowedSenders === 'SCHOOL' && (
          <Field
            label="Other school email domains"
            hint="People in your directory, anyone with an Edvora account here, and addresses on your Google sign-in domains always count. Add any other domains your school uses, separated by commas."
          >
            <Textarea
              rows={2}
              value={allowedDomains}
              onChange={(e) => setAllowedDomains(e.target.value)}
              disabled={!isAdmin}
              className="font-mono text-xs"
              placeholder="yourschool.org, students.yourschool.org"
            />
          </Field>
        )}
        <Field
          label={facilities ? 'Category for new requests' : 'Department for new tickets'}
          hint={facilities ? 'Routing rules assign requests from here.' : 'Workflows can still route tickets from here.'}
        >
          <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} disabled={!isAdmin}>
            <option value="">{facilities ? 'No category' : 'No department'}</option>
            {departments.data?.map((department) => (
              <option key={department.id} value={department.id}>
                {indentedName(department)}
              </option>
            ))}
          </Select>
        </Field>
        <Switch
          label="Confirm new tickets by email"
          description="Replies to the sender with the ticket number, so their next reply goes on the same ticket."
          checked={sendConfirmation}
          disabled={!isAdmin}
          onChange={setSendConfirmation}
        />
        <Field
          label="Email technicians’ replies to requesters"
          hint="Public replies go out from the help desk mailbox, and the requester’s answer comes back to the ticket. Internal notes are never emailed."
        >
          <Select value={replyByEmail} onChange={(e) => setReplyByEmail(e.target.value as typeof replyByEmail)} disabled={!isAdmin}>
            <option value="ALL_TICKETS">On every ticket</option>
            <option value="EMAILED_TICKETS">Only on tickets that came in by email</option>
            <option value="OFF">Don’t email replies</option>
          </Select>
        </Field>
        <Switch
          label="Email technicians when a requester replies by email"
          description="The technician assigned to the ticket, and the one being answered, get the reply and can answer it from their inbox."
          checked={notifyTechnicians}
          disabled={!isAdmin}
          onChange={setNotifyTechnicians}
        />
        <Switch
          label="Email the requester when their ticket is resolved"
          description={
            replyByEmail === 'OFF'
              ? 'Choose which tickets get replies by email first.'
              : 'Sends the resolution, and tells them a reply reopens the ticket if it isn’t fixed.'
          }
          checked={emailOnResolve && replyByEmail !== 'OFF'}
          disabled={!isAdmin || replyByEmail === 'OFF'}
          onChange={setEmailOnResolve}
        />
        {facilities && (
          <Switch
            label="Email the requester when work starts or is on hold"
            description="Lets them know someone is working on it, or that it’s waiting on something."
            checked={emailStatusUpdates}
            disabled={!isAdmin}
            onChange={setEmailStatusUpdates}
          />
        )}
        <Switch
          label="Reopen resolved tickets when the requester replies"
          description="A reply to a closed ticket always opens a new follow-up ticket instead."
          checked={reopenOnReply}
          disabled={!isAdmin}
          onChange={setReopenOnReply}
        />
        {isAdmin && (
          <Button
            variant="primary"
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                {
                  settings: {
                    allowedSenders,
                    allowedDomains,
                    departmentId,
                    sendConfirmation,
                    replyByEmail,
                    notifyTechnicians,
                    emailOnResolve,
                    reopenOnReply,
                    ...(facilities ? { emailStatusUpdates } : {}),
                  },
                },
                { onSuccess: (saved) => setAllowedDomains((saved as EmailIntegration).settings.allowedDomains.join(', ')) },
              )
            }
          >
            Save
          </Button>
        )}
      </div>

      <aside className="h-fit rounded-lg border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
        <h3 className="font-semibold text-slate-900">How email and tickets connect</h3>
        <ul className="mt-2 list-disc space-y-1.5 pl-5">
          <li>A new email opens a ticket. The subject becomes the title and the message the description.</li>
          <li>Replies go on the ticket they belong to, matched by the ticket number in the subject, like [#1042], the email thread, or the email they answer.</li>
          <li>Technicians’ public replies are emailed to the requester. When the requester answers, the technicians on the ticket get it by email and can reply straight from their inbox.</li>
          <li>When a ticket is resolved, the requester gets the resolution by email.</li>
          <li>
            Only the requester and {facilities ? 'the facilities team' : 'the technology team'} can add to a ticket by email. Anyone else at the school gets a ticket
            of their own.
          </li>
          {facilities ? <li>New requests go through your routing rules, so they reach the right crew.</li> : <li>An asset tag in the email links the ticket to that device.</li>}
          <li>Automatic replies, bounces, mailing lists, and mail from outside the school are skipped. The email log shows why.</li>
          <li>Attachments stay in the mailbox; the ticket notes that there were some.</li>
        </ul>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email log
// ---------------------------------------------------------------------------

type Tone = 'green' | 'blue' | 'gray' | 'red' | 'amber';

const OUTCOME: Record<InboundEmailOutcome, { label: string; tone: Tone }> = {
  TICKET_CREATED: { label: 'New ticket', tone: 'green' },
  COMMENT_ADDED: { label: 'Added to ticket', tone: 'blue' },
  IGNORED: { label: 'Skipped', tone: 'gray' },
  FAILED: { label: 'Failed', tone: 'red' },
};

const SENT_KIND: Record<OutboundEmailKind, string> = {
  CONFIRMATION: 'Confirmation',
  REQUESTER_REPLY: 'Reply to the requester',
  TECHNICIAN_NOTICE: 'Notice to a technician',
  RESOLUTION: 'Resolution notice',
  STATUS_UPDATE: 'Status update',
};

const SENT_STATUS: Record<OutboundEmailStatus, { label: string; tone: Tone }> = {
  QUEUED: { label: 'Waiting to send', tone: 'gray' },
  SENDING: { label: 'Sending', tone: 'blue' },
  SENT: { label: 'Sent', tone: 'green' },
  FAILED: { label: 'Not sent', tone: 'red' },
  CANCELED: { label: 'Canceled', tone: 'gray' },
};

const LOG_PAGE_SIZE = 25;

function TicketLink({ ticket, provider }: { ticket: { id: string; number: number } | null; provider: IntegrationProvider }) {
  if (!ticket) return null;
  return (
    <Link to={workOrderHref(provider === 'FACILITIES_EMAIL' ? 'FACILITIES' : 'IT', ticket.id)} className="text-xs font-medium text-brand-700 hover:underline">
      #{ticket.number}
    </Link>
  );
}

function ReceivedEmails({ provider }: { provider: IntegrationProvider }) {
  const [page, setPage] = useState(1);
  const [outcome, setOutcome] = useState<InboundEmailOutcome | ''>('');
  const log = useQuery({
    queryKey: ['integrations', 'email-log', provider, page, outcome],
    queryFn: () => api.get<Page<InboundEmail>>(`/integrations/${providerPath(provider)}/log`, { page, pageSize: LOG_PAGE_SIZE, ...(outcome ? { outcome } : {}) }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Each email read from the mailbox and what happened to it. Skipped and failed emails are listed for 90 days.</p>
        <Select
          className="w-44"
          value={outcome}
          onChange={(e) => {
            setOutcome(e.target.value as InboundEmailOutcome | '');
            setPage(1);
          }}
        >
          <option value="">All emails</option>
          {(Object.keys(OUTCOME) as InboundEmailOutcome[]).map((key) => (
            <option key={key} value={key}>
              {OUTCOME[key].label}
            </option>
          ))}
        </Select>
      </div>
      {log.isLoading ? (
        <Spinner />
      ) : log.error ? (
        <Alert tone="red">{errorMessage(log.error)}</Alert>
      ) : !log.data || log.data.total === 0 ? (
        <EmptyState title="No emails yet" description={outcome ? 'No emails match this filter.' : 'Emails show up here once the mailbox has been checked.'} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <Table>
            <thead>
              <tr>
                <Th>Received</Th>
                <Th>From</Th>
                <Th>Subject</Th>
                <Th>Result</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {log.data.items.map((item) => (
                <tr key={item.id}>
                  <Td className="text-xs whitespace-nowrap text-slate-500">{formatDateTime(item.receivedAt)}</Td>
                  <Td className="max-w-[14rem]">
                    <p className="truncate font-medium text-slate-900">{item.fromName ?? item.fromAddress}</p>
                    {item.fromName && <p className="truncate text-xs text-slate-500">{item.fromAddress}</p>}
                  </Td>
                  <Td className="max-w-xs">
                    <p className="truncate">{item.subject}</p>
                  </Td>
                  <Td className="min-w-[12rem]">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={OUTCOME[item.outcome].tone}>{OUTCOME[item.outcome].label}</Badge>
                      <TicketLink ticket={item.workOrder} provider={provider} />
                    </div>
                    {item.reason && <p className="mt-1 text-xs text-slate-500">{item.reason}</p>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={page} pageSize={LOG_PAGE_SIZE} total={log.data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}

function SentEmails({ provider }: { provider: IntegrationProvider }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<OutboundEmailStatus | ''>('');
  const outbox = useQuery({
    queryKey: ['integrations', 'email-outbox', provider, page, status],
    queryFn: () => api.get<Page<OutboundEmail>>(`/integrations/${providerPath(provider)}/outbox`, { page, pageSize: LOG_PAGE_SIZE, ...(status ? { status } : {}) }),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (q.state.data?.items.some((email) => email.status === 'QUEUED' || email.status === 'SENDING') ? 5000 : false),
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/integrations/${providerPath(provider)}/outbox/${id}/retry`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'email-outbox'] });
      toast.success('It’ll be sent again in a moment');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Email sent from the mailbox. An email that can’t be sent is tried 4 more times over about 80 minutes.</p>
        <Select
          className="w-44"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as OutboundEmailStatus | '');
            setPage(1);
          }}
        >
          <option value="">All emails</option>
          {(Object.keys(SENT_STATUS) as OutboundEmailStatus[]).map((key) => (
            <option key={key} value={key}>
              {SENT_STATUS[key].label}
            </option>
          ))}
        </Select>
      </div>
      {outbox.isLoading ? (
        <Spinner />
      ) : outbox.error ? (
        <Alert tone="red">{errorMessage(outbox.error)}</Alert>
      ) : !outbox.data || outbox.data.total === 0 ? (
        <EmptyState title="Nothing sent yet" description={status ? 'No emails match this filter.' : 'Confirmations, replies, and notices show up here.'} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <Table>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>To</Th>
                <Th>Subject</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {outbox.data.items.map((email) => {
                const retrying = email.status === 'QUEUED' && !!email.error;
                const state = retrying ? { label: 'Retrying', tone: 'amber' as const } : SENT_STATUS[email.status];
                return (
                  <tr key={email.id}>
                    <Td className="text-xs whitespace-nowrap text-slate-500">{formatDateTime(email.sentAt ?? email.createdAt)}</Td>
                    <Td className="max-w-[14rem]">
                      <p className="truncate font-medium text-slate-900">{email.toName ?? email.toAddress}</p>
                      {email.toName && <p className="truncate text-xs text-slate-500">{email.toAddress}</p>}
                    </Td>
                    <Td className="max-w-xs">
                      <p className="truncate">{email.subject}</p>
                      <p className="text-xs text-slate-500">{SENT_KIND[email.kind]}</p>
                    </Td>
                    <Td className="min-w-[12rem]">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={state.tone}>{state.label}</Badge>
                        <TicketLink ticket={email.workOrder} provider={provider} />
                        {(email.status === 'FAILED' || email.status === 'CANCELED') && (
                          <Button size="sm" variant="ghost" loading={retry.isPending && retry.variables === email.id} onClick={() => retry.mutate(email.id)}>
                            Try again
                          </Button>
                        )}
                      </div>
                      {email.error && <p className="mt-1 text-xs text-slate-500">{email.error}</p>}
                      {retrying && <p className="text-xs text-slate-500">Next try {formatDateTime(email.nextAttemptAt)}</p>}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Pagination page={page} pageSize={LOG_PAGE_SIZE} total={outbox.data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}

export function EmailLog({ provider = 'EMAIL' }: { provider?: IntegrationProvider }) {
  const [direction, setDirection] = useState<'received' | 'sent'>('received');
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-sm">
        {(['received', 'sent'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setDirection(value)}
            className={cn('rounded-md px-3 py-1 font-medium', direction === value ? 'bg-brand-50 text-brand-800' : 'text-slate-500 hover:text-slate-700')}
          >
            {value === 'received' ? 'Received' : 'Sent'}
          </button>
        ))}
      </div>
      {direction === 'received' ? <ReceivedEmails provider={provider} /> : <SentEmails provider={provider} />}
    </div>
  );
}
